import { z } from 'zod';

import {
  AdminNotificationCategorySchema,
  AdminNotificationSeveritySchema,
  MinimumSeveritySchema,
  ReadStatusFilterSchema,
} from './enums';

// ============================================================================
// List Admin Notifications — GET /api/v1/admin/notifications
// ============================================================================

export const ListAdminNotificationsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  category: AdminNotificationCategorySchema.optional(),
  severity: AdminNotificationSeveritySchema.optional(),
  readStatus: ReadStatusFilterSchema,
});

export type ListAdminNotificationsQuery = z.infer<typeof ListAdminNotificationsQuerySchema>;

export const AdminNotificationItemSchema = z.object({
  id: z.string().uuid(),
  type: z.string(),
  category: AdminNotificationCategorySchema,
  severity: AdminNotificationSeveritySchema,
  title: z.string(),
  message: z.string(),
  actionUrl: z.string().nullable(),
  metadata: z.unknown().nullable(),
  sourceService: z.string().nullable(),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime().nullable(),
  isRead: z.boolean(),
  readAt: z.string().datetime().nullable(),
});

export type AdminNotificationItem = z.infer<typeof AdminNotificationItemSchema>;

export const ListAdminNotificationsResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(AdminNotificationItemSchema),
  meta: z.object({
    page: z.number().int(),
    limit: z.number().int(),
    total: z.number().int(),
    totalPages: z.number().int(),
    hasMore: z.boolean(),
    unreadCount: z.number().int(),
  }),
});

export type ListAdminNotificationsResponse = z.infer<typeof ListAdminNotificationsResponseSchema>;

// ============================================================================
// Unread Count — GET /api/v1/admin/notifications/unread-count
// ============================================================================

export const UnreadCountResponseSchema = z.object({
  count: z.number().int(),
});

export type UnreadCountResponse = z.infer<typeof UnreadCountResponseSchema>;

// ============================================================================
// Mark All As Read — PATCH /api/v1/admin/notifications/read-all
// ============================================================================

export const MarkAllReadResponseSchema = z.object({
  markedCount: z.number().int(),
});

export type MarkAllReadResponse = z.infer<typeof MarkAllReadResponseSchema>;

// ============================================================================
// Notification Preferences — GET/PUT /api/v1/admin/notifications/preferences
// ============================================================================

export const UpdatePreferencesRequestSchema = z.object({
  capacityEnabled: z.boolean().optional(),
  paymentEnabled: z.boolean().optional(),
  deviceEnabled: z.boolean().optional(),
  securityEnabled: z.boolean().optional(),
  minimumSeverity: MinimumSeveritySchema.optional(),
});

export type UpdatePreferencesRequest = z.infer<typeof UpdatePreferencesRequestSchema>;

export const NotificationPreferencesResponseSchema = z.object({
  userId: z.string().uuid(),
  capacityEnabled: z.boolean(),
  paymentEnabled: z.boolean(),
  deviceEnabled: z.boolean(),
  securityEnabled: z.boolean(),
  minimumSeverity: MinimumSeveritySchema,
  updatedAt: z.string().datetime(),
});

export type NotificationPreferencesResponse = z.infer<typeof NotificationPreferencesResponseSchema>;
